import { LineaVenta } from "../models/LineaVentaModel";
import { UserPayload } from "../models/SiteModel";
import { parseDecimalValue } from "../utils/NumberParser";
import { apiFetch } from "./fetchService";

const URLAPI = process.env.REACT_APP_BASE_URL;

export interface CierreVentaWebConfiguration {
  habilitado: boolean;
  pvOperacionesWeb?: number;
  permiteProforma?: boolean;
  permiteFacturaElectronica?: boolean;
  permiteListaPrecio2?: boolean;
  permiteListaPrecio3?: boolean;
  mensaje: string;
}

export async function fetchConfiguracionCierreVentaWeb(user: UserPayload, idLocal: number): Promise<CierreVentaWebConfiguration> {
  const data = await apiFetch(user.token, `${URLAPI}/Venta/ObtenerConfiguracionCierreWeb?idLocal=${idLocal}`);
  return {
    habilitado: Boolean(data.habilitado ?? data.Habilitado),
    pvOperacionesWeb: Number(data.pvOperacionesWeb ?? data.PvOperacionesWeb) || undefined,
    permiteProforma: Boolean(data.permiteProforma ?? data.PermiteProforma),
    permiteFacturaElectronica: Boolean(data.permiteFacturaElectronica ?? data.PermiteFacturaElectronica),
    permiteListaPrecio2: Boolean(data.permiteListaPrecio2 ?? data.PermiteListaPrecio2),
    permiteListaPrecio3: Boolean(data.permiteListaPrecio3 ?? data.PermiteListaPrecio3),
    mensaje: String(data.mensaje ?? data.Mensaje ?? ""),
  };
}

export type CerrarProformaWebResult = {
  success: boolean;
  idOperacion?: number;
  numeroComprobante?: number;
  total?: number;
  message: string;
  requiereAutorizacionCuentaCorriente?: boolean;
};

export type AutorizacionCuentaCorrienteWeb = {
  idAutorizacion: number;
  usuarioAutorizante: string;
  fechaCaducidad: string;
};

export class CerrarProformaWebError extends Error {
  constructor(message: string, public readonly requiereAutorizacion: boolean) {
    super(message);
    this.name = "CerrarProformaWebError";
  }
}

export async function fetchSolicitarAutorizacionCuentaCorrienteWeb(user: UserPayload): Promise<number> {
  const response = await fetch(`${URLAPI}/Venta/SolicitarAutorizacionCuentaCorrienteWeb`, {
    method: "POST",
    headers: { Authorization: `Bearer ${user.token}` },
  });
  const data = await response.json();
  if (!response.ok) throw new Error(String(data.mensaje ?? data.Message ?? "No se pudo solicitar la autorización."));
  return Number(data.idAutorizacion ?? data.IdAutorizacion) || 0;
}

export async function fetchAutorizacionesCuentaCorrienteWeb(user: UserPayload): Promise<AutorizacionCuentaCorrienteWeb[]> {
  const data = await apiFetch(user.token, `${URLAPI}/Venta/ObtenerAutorizacionesCuentaCorrienteWeb`);
  return (Array.isArray(data) ? data : []).map((item: any) => ({
    idAutorizacion: Number(item.idAutorizacion ?? item.IdAutorizacion) || 0,
    usuarioAutorizante: String(item.usuarioAutorizante ?? item.UsuarioAutorizante ?? ""),
    fechaCaducidad: String(item.fechaCaducidad ?? item.FechaCaducidad ?? ""),
  })).filter((item: AutorizacionCuentaCorrienteWeb) => item.idAutorizacion > 0);
}

export async function fetchCerrarProformaWeb(
  user: UserPayload,
  request: { idLocal: number; idCliente: number; listaPrecio: number; claveIdempotencia: string; idAutorizacionCuentaCorriente?: number; lineas: { idProducto: number; cantidad: number }[] }
): Promise<CerrarProformaWebResult> {
  const response = await fetch(`${URLAPI}/Venta/CerrarProformaWeb`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${user.token}` },
    body: JSON.stringify(request),
  });
  const data = await response.json();
  const result = {
    success: Boolean(data.success ?? data.Success),
    idOperacion: Number(data.idOperacion ?? data.IdOperacion) || undefined,
    numeroComprobante: Number(data.numeroComprobante ?? data.NumeroComprobante) || undefined,
    total: Number(data.total ?? data.Total) || undefined,
    message: String(data.message ?? data.Message ?? "No se pudo cerrar la proforma."),
    requiereAutorizacionCuentaCorriente: Boolean(data.requiereAutorizacionCuentaCorriente ?? data.RequiereAutorizacionCuentaCorriente),
  };
  if (!response.ok) throw new CerrarProformaWebError(result.message, result.requiereAutorizacionCuentaCorriente ?? false);
  return result;
}

const getProductoPrecio = (producto: any) =>
  parseDecimalValue(producto.CONTADO ?? producto.contado ?? producto.Contado ?? producto.precio ?? producto.Precio ?? producto.precioContado ?? producto.PrecioContado ?? 0);

export type GuardarVentaOptions = {
  numeroPv?: number;
  idCaja?: number;
  cliente?: string;
  total?: number;
  formaPago?: string;
  descuento?: number;
  recargo?: number;
  observacion?: string;
  ldp?: string;
  numeroMesa?: string;
  origen?: string;
  notificarWindowsForms?: boolean;
};

const mapLineaVentaToPayload = (linea: LineaVenta) => ({
  Id_Lineaventa: 0,
  Id_Venta: 0,
  Id_Producto: (linea.producto as any).IDPRODUCTO ?? (linea.producto as any).idProducto ?? (linea.producto as any).IdProducto,
  Cod_Prod: (linea.producto as any).CODIGO ?? (linea.producto as any).codigo ?? (linea.producto as any).Codigo,
  Producto: (linea.producto as any).PRODUCTO ?? (linea.producto as any).producto ?? (linea.producto as any).Producto,
  Cantidad: linea.cantidad,
  Precio_Unitario: getProductoPrecio(linea.producto),
  Precio_Real: getProductoPrecio(linea.producto),
  Notas: linea.notas || ""
});

export async function fetchGuardarVenta(user: UserPayload,
    idCliente: number, idLocal: number, lineaventa:LineaVenta[], options?: GuardarVentaOptions | string
) {
    const token = user.token;
    const payload = lineaventa.map(mapLineaVentaToPayload);
    const normalizedOptions: GuardarVentaOptions = typeof options === "string"
      ? { numeroMesa: options }
      : options ?? {};
    const postData = JSON.stringify({
        IdLocal: idLocal,
        IdCliente: idCliente,
        NumeroPv: normalizedOptions.numeroPv,
        IdCaja: normalizedOptions.idCaja,
        Cliente: normalizedOptions.cliente,
        Total: normalizedOptions.total,
        FormaPago: normalizedOptions.formaPago,
        Descuento: normalizedOptions.descuento,
        Recargo: normalizedOptions.recargo,
        Observacion: normalizedOptions.observacion,
        Ldp: normalizedOptions.ldp,
        NumeroMesa: normalizedOptions.numeroMesa,
        Mesa: normalizedOptions.numeroMesa,
        Origen: normalizedOptions.origen,
        NotificarWindowsForms: normalizedOptions.notificarWindowsForms,
        LineaVenta: payload
    });
    const response = await fetch(
      `${URLAPI}/Venta/GuardarVenta`,
      {
        method: "POST", headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: postData
      }
    );
  
    const data = await response.json();
    return data;
  }

export async function fetchGetMesaAbierta(user: UserPayload, idLocal: number, numeroMesa: string) {
  const token = user.token;
  const response = await fetch(
    `${URLAPI}/Venta/GetMesaAbierta?idLocal=${idLocal}&numeroMesa=${encodeURIComponent(numeroMesa)}`,
    {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + token
      },
      body: JSON.stringify({
        IdLocal: idLocal,
        NumeroMesa: numeroMesa,
        Mesa: numeroMesa
      })
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  const data = await response.json();
  return data;
}
